import React, {FC} from 'react';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf, Renderer} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownBoxCoordinator} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {Dropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {DropdownList} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';

/*
 * Props.
 */

interface DurationDropdownBoxProps {
  duration: number;
  onDurationSelect: HandlerOf<number>;
  options: ReadonlyArray<number>;
  renderNullValue?: Renderer;
  className?: string;
  maxHeight?: number;
}

/*
 * Component.
 */

export const DurationDropdownBox: FC<DurationDropdownBoxProps> = (props) => (
  <DropdownBoxCoordinator
    value={renderItem(props.duration, props.renderNullValue)}
    renderDropdown={(popoverProps) => renderDropdown(popoverProps, props)}
    boxClassName={props.className}
  />
);

function renderDropdown(popoverProps: PopoverTransferProps, props: DurationDropdownBoxProps) {
  return (
    <Dropdown {...popoverProps} maxHeight={props.maxHeight}>
      {renderDropdownContent(popoverProps, props)}
    </Dropdown>
  );
}

function renderDropdownContent(popoverProps: PopoverTransferProps, props: DurationDropdownBoxProps) {
  return (
    <DropdownList {...popoverProps}>
      {props.options.map((duration) => {
        const isSelected = duration === props.duration;
        return (
          <DropdownItem
            key={duration}
            onSelect={() => props.onDurationSelect(duration)}
            isChecked={isSelected}
          >
            <DropdownItemContent isChecked={isSelected}>
              {renderItem(duration, props.renderNullValue)}
            </DropdownItemContent>
          </DropdownItem>
        );
      })}
    </DropdownList>
  );
}

function renderItem(duration: number, renderNullValue: Renderer | undefined) {
  if (duration === 0 && renderNullValue) {
    return renderNullValue();
  }

  if (duration < 60) {
    return <Trans id="scheduling-duration-minutes">{duration} minutes</Trans>;
  }

  if (duration === 60) {
    return <Trans id="scheduling-duration-hour">1 hour</Trans>;
  }

  if (duration < 120) {
    return <Trans id="scheduling-duration-hourminutes">1 hour {duration - 60} minutes</Trans>;
  }

  if (duration < 60 * 24 && duration % 60 === 0) {
    return <Trans id="scheduling-duration-hours">{duration / 60} hours</Trans>;
  }

  if (duration < 60 * 24) {
    return (
      <Trans id="scheduling-duration-hoursminutes">
        {Math.floor(duration / 60)} hours {duration % 60} minutes
      </Trans>
    );
  }

  if (duration === 60 * 24) {
    return <Trans id="scheduling-duration-day">1 day</Trans>;
  }

  if (duration < 60 * 24 * 7) {
    return <Trans id="scheduling-duration-days">{duration / 60 / 24} days</Trans>;
  }

  return <Trans id="scheduling-duration-week">1 week</Trans>;
}
